// One brand-asset (signature OR stamp) upload control for the admin
// settings screen. CLAUDE.md rule #7: the image is admin-only and lives in
// private storage — this component loads it into an <img> straight from
// GET /admin/settings/brand-assets/:kind/image (the one admin-only path
// that serves the bytes); the session cookie authenticates. The src goes
// through apiClient.url so a split-origin deployment points the <img> at
// the API host, not the static frontend (which would return index.html).
//
// Shows the current image against a LIGHT and a DARK background so a
// missing alpha channel (a white box that would print over the proforma)
// is obvious before it reaches a document. Replace and Remove both go
// through the shared confirm dialog; upload/replace/remove each write to
// audit_log server-side.

import { useRef } from 'react';
import apiClient from 'shared/api/client.js';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useUploadBrandAsset, useRemoveBrandAsset } from '../hooks/useBrandAssets.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import { formatDateTime } from '../utils/formatDate.js';
import styles from './BrandAssetUpload.module.css';

const KNOWN_CODES = [
  'INVALID_BRAND_ASSET',
  'INVALID_BRAND_ASSET_TYPE',
  'BRAND_ASSET_TOO_LARGE',
  'BRAND_ASSET_NOT_FOUND',
  'FILE_REQUIRED',
  'UPLOAD_ERROR',
];

export default function BrandAssetUpload({ kind, asset }) {
  const { t, lang } = useLanguage();
  const { confirm, toastOk, toastErr } = useActionFeedback();
  const uploadMutation = useUploadBrandAsset(kind);
  const removeMutation = useRemoveBrandAsset(kind);
  const fileInputRef = useRef(null);

  const present = Boolean(asset && asset.present);
  const recommended = asset && asset.recommended;
  const busy = uploadMutation.isPending || removeMutation.isPending;

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    uploadMutation.mutate(file, {
      onSuccess: () => toastOk(t(`settings.branding.${kind}.savedToast`)),
      onError: (err) => toastErr(err, KNOWN_CODES),
    });
  };

  const handleReplace = async () => {
    const ok = await confirm({
      title: t(`settings.branding.${kind}.replaceConfirmTitle`),
      message: t('settings.branding.replaceConfirmBody'),
      confirmLabel: t('settings.branding.replaceConfirm'),
      tone: 'danger',
    });
    if (ok) fileInputRef.current?.click();
  };

  const handleRemove = async () => {
    const ok = await confirm({
      title: t(`settings.branding.${kind}.removeConfirmTitle`),
      message: t('settings.branding.removeConfirmBody'),
      confirmLabel: t('settings.branding.removeConfirm'),
      tone: 'danger',
    });
    if (!ok) return;
    removeMutation.mutate(undefined, {
      onSuccess: () => toastOk(t(`settings.branding.${kind}.removedToast`)),
      onError: (err) => toastErr(err, KNOWN_CODES),
    });
  };

  const alphaWarning = present && asset.format === 'png' && !asset.hasAlpha;
  const jpgNote = present && asset.format === 'jpg';
  const imgSrc = apiClient.url(`/admin/settings/brand-assets/${kind}/image`, {
    v: (asset && asset.updatedAt) || '',
  });

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{t(`settings.branding.${kind}.title`)}</h3>
      {recommended && (
        <p className={styles.hint}>
          {t('settings.branding.recommended', {
            width: recommended.width,
            height: recommended.height,
          })}
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        className={styles.hiddenInput}
        onChange={handleFileChange}
        disabled={busy}
      />

      {present ? (
        <>
          <div className={styles.previews}>
            <div className={`${styles.previewBox} ${styles.previewLight}`}>
              <img
                src={imgSrc}
                alt={t(`settings.branding.${kind}.previewLightAlt`)}
                className={styles.previewImg}
              />
            </div>
            <div className={`${styles.previewBox} ${styles.previewDark}`}>
              <img
                src={imgSrc}
                alt={t(`settings.branding.${kind}.previewDarkAlt`)}
                className={styles.previewImg}
              />
            </div>
          </div>

          <p className={styles.meta}>
            {t('settings.branding.meta', {
              format: asset.format.toUpperCase(),
              width: asset.width ?? '?',
              height: asset.height ?? '?',
            })}
            {asset.updatedAt ? ` · ${formatDateTime(asset.updatedAt, lang) || ''}` : ''}
          </p>

          {alphaWarning && (
            <p className={styles.warning} role="alert">
              {t('settings.branding.alphaWarning')}
            </p>
          )}
          {jpgNote && <p className={styles.note}>{t('settings.branding.jpgNote')}</p>}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.replaceButton}
              onClick={handleReplace}
              disabled={busy}
            >
              {uploadMutation.isPending && <Spinner />} {t('settings.branding.replace')}
            </button>
            <button
              type="button"
              className={styles.removeButton}
              onClick={handleRemove}
              disabled={busy}
            >
              {removeMutation.isPending && <Spinner />} {t('settings.branding.remove')}
            </button>
          </div>
        </>
      ) : (
        <button
          type="button"
          className={styles.uploadButton}
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
        >
          {uploadMutation.isPending && <Spinner />} {t('settings.branding.upload')}
        </button>
      )}
    </div>
  );
}
